import { Alert, Alignment, Button, Direction, Displaying, Spacing } from "@bonusx-global/bx-design-system";
import { ArrowsRepeatOutlined } from "@bonusx-global/bx-icons";
import styled from "@emotion/styled";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import { useRef } from "react";
import { API_GENERAL_ERROR_TEXT } from "../../../../../constants/commonStrings";
import { ZERO_DIGIT } from "../../../../../constants/env";
import type { IWithIsMobile } from "../../../../interfaces/IWithIsMobile.interface";
import { FetchStrategies } from "../../../../../features/ai-chatbot/constants/fetch-strategies.enum";
import AiChatbotSuggestedPromptWrapper from "../../../AiChatbotSuggestedPrompt/wrappers/ai-chatbot-suggested-prompt-wrapper";
import { EmptyComponent } from "../../../common/EmptyComponent";
import TextLaborIllusion from "../../../LaborIllusion/text-labor-illusion";
import { CHATBOT_LABOR_ILLUSION_MESSAGES } from "../../../LaborIllusion/text-labor-illusion.const";
import type { IChatbotMessage } from "../../entities/chatbot-message.entity";
import { SenderTypeEnum } from "../../enums/sender-type.enum";
import { useChatbotMessagesAutoScroll } from "../../hooks/use-chatbot-messages-auto-scroll/use-chatbot-messages-auto-scroll";
import { ChatbotMessageItem } from "./chatbot.message-item";

interface MessagesListProps {
  messages: IChatbotMessage[];
  isMobile: boolean;
  hasLastChatButton: boolean;
  isSending?: boolean;
  onClickLastChatButton?: () => void;
  errorWhileGeneratingResponse?: boolean;
  errorMessage?: string;
  handleOnRetry?: () => Promise<void>;
  showRetryButton?: boolean;
  hideRetryButton?: boolean;
  retryButtonText?: string;
}

const CHATBOT_MESSAGES_CONTAINER_ID = "chatbot-messages-container";
const DEFAULT_ERROR_MESSAGE = "Si è verificato un errore durante la generazione della risposta.";
const DEFAULT_RETRY_BUTTON_TEXT = "Riprova";

const ChatbotMessagesContainer = styled("div")<IWithIsMobile>(({ isMobile }) => ({
  flexGrow: 1,
  scrollbarGutter: "stable",
  overflow: isMobile ? "auto" : "hidden",
  padding: isMobile
    ? `${Spacing["1500"]} ${Spacing["1000"]} 4px ${Spacing["1000"]}`
    : `${Spacing["1500"]} ${Spacing["500"]} ${Spacing["750"]} ${Spacing["1500"]}`,
  display: Displaying.flex,
  flexDirection: Direction.column,
  gap: Spacing[1500],
  "&:hover": {
    overflowY: "auto",
  },
}));

export function ChatbotMessageList({
  messages,
  isMobile,
  isSending,
  hasLastChatButton,
  onClickLastChatButton,
  errorWhileGeneratingResponse,
  errorMessage,
  handleOnRetry,
  showRetryButton = true,
  retryButtonText = DEFAULT_RETRY_BUTTON_TEXT,
}: Readonly<MessagesListProps>) {
  const chatContainerRef = useRef<HTMLDivElement | null>(null);
  const lastUserMessageRef = useRef<HTMLDivElement | null>(null);

  const { handleContainerScroll } = useChatbotMessagesAutoScroll({ chatContainerRef });

  const renderMessageItem = (message: IChatbotMessage, index: number) => {
    const isUserMessage = message.senderType === SenderTypeEnum.USER;
    const isLastMessage = index === messages.length - 1;
    const isLastUserMessage = isUserMessage && isLastMessage;

    return (
      <Box
        key={index}
        ref={isLastUserMessage ? lastUserMessageRef : null}
        display={Displaying.flex}
        justifyContent={isUserMessage ? Alignment.flexEnd : Alignment.flexStart}
      >
        <ChatbotMessageItem key={index} message={message} />
      </Box>
    );
  };

  const loadingResponse = isSending ? (
    <TextLaborIllusion messages={CHATBOT_LABOR_ILLUSION_MESSAGES} />
  ) : (
    <EmptyComponent />
  );

  return (
    <ChatbotMessagesContainer
      id={CHATBOT_MESSAGES_CONTAINER_ID}
      ref={chatContainerRef}
      isMobile={isMobile}
      onScroll={handleContainerScroll}
    >
      {messages.length === ZERO_DIGIT ? (
        <AiChatbotSuggestedPromptWrapper
          hasLastChatButton={hasLastChatButton}
          onClickLastChatButton={onClickLastChatButton}
          fetchStrategy={FetchStrategies.inChat}
          flexDirection={isMobile ? Direction.row : Direction.column}
        />
      ) : (
        <>
          {messages.map(renderMessageItem)}

          {errorWhileGeneratingResponse ? (
            <Alert
              vertical
              severity="error"
              action={
                showRetryButton && handleOnRetry ? (
                  <Button size="small" color="error" endIcon={<ArrowsRepeatOutlined />} onClick={handleOnRetry}>
                    {retryButtonText}
                  </Button>
                ) : null
              }
            >
              <AlertTitle>{API_GENERAL_ERROR_TEXT}</AlertTitle>
              {errorMessage || DEFAULT_ERROR_MESSAGE}
            </Alert>
          ) : (
            loadingResponse
          )}
        </>
      )}
    </ChatbotMessagesContainer>
  );
}
